import React, { useState, useMemo, useCallback, useRef } from 'react';
import { Redirect } from 'react-router-dom';
import { connect } from 'react-redux';
import { withStyles, Tooltip, IconButton, Typography } from '@material-ui/core';
import { hasPermission, hasAtLeastOnePermission, createHistory } from '../../sc-utilities';
import CustomerErasureMethodList from './CustomerErasureMethodList';
import { FindInPage } from '@material-ui/icons';

const styles = theme => ({
    root: {
        width: `98.9%`,
        height: 'auto',
        paddingTop: '0.6rem',
        paddingLeft: theme.spacing(),
        [theme.breakpoints.down('sm')]: {
            padding: 0,
            margin: 0,
            width: '100%'
        }
    }
});

const CustomerErasureMethod = props => {
    const tableRef = useRef();
    const { classes, columns, translations, permissions } = props;
    const [redirectOptions, setRedirectOptions] = useState({ redirect: false, ErasureMethodID: null, redirectPath: '' });

    const getDedicatedFor = useCallback(dedicatedFor => {
        switch (dedicatedFor) {
            case 'magnetic':
                return <Typography variant={'caption'}>{translations['magnetic'] || 'Magnetic'}</Typography>;
            case 'flash':
                return <Typography variant={'caption'}>{translations['flash'] || 'Flash'}</Typography>;
            case 'all':
                return <Typography variant={'caption'}>{translations['all'] || 'All'}</Typography>;
            default:
                return '';
        }
    }, [translations]);

    let visible = columns && columns['CustomerErasureMethod'];
    const tableColumns = useMemo(() => {
        return [
            {
                title: translations["action"] || "Action", field: 'Action',
                hidden: visible ? visible['Action'] : false,
                cellStyle: { minWidth: 100, maxWidth: 100 },
                filtering: false,
                render: rowData => <>
                    {
                        hasPermission(permissions, 'customer_erasure_method_load') ? <Tooltip title={translations["erasure_method_details"] || "Erasure method details"}>
                            <IconButton value={rowData.ErasureMethodID} aria-label={translations["erasure_method_details"] || "Erasure method details"} onClick={() => redirectHandler(`/settings/customer-erasure-method/edit?ErasureMethodID=${rowData.ErasureMethodID}`)}>
                                <FindInPage />
                            </IconButton>
                        </Tooltip> : ''
                    }
                </>
            },
            {
                title: translations["key"] || 'Key', field: 'Key',
                hidden: visible ? visible['Key'] : !hasPermission(permissions, 'erasure_method_load'),
                filtering: true,
                render: rowData => {
                    return <Typography variant={'caption'}>{rowData.Key ? rowData.Key : translations['short_not_assigned'] || 'n/a'}</Typography>;
                }
            },
            {
                title: translations["visible"] || 'Visible', field: 'Visible',
                hidden: visible ? visible['Visible'] : false,
                lookup: { '1': translations['yes'] || 'Yes', '0': translations['no'] || 'No' },
                defaultFilter: ['0', '1'],
                filtering: true,
                render: rowData => {
                    return <Typography variant={'caption'}>{rowData.Visible ? translations['yes'] || 'Yes' : translations['no'] || 'No'}</Typography>;
                }
            },
            {
                title: translations["long_name"] || 'Long name', field: 'LongName',
                hidden: visible ? visible['LongName'] : false,
                filtering: true,
                width: 200,
                render: rowData => {
                    return <Typography variant={'caption'}>{rowData.LongName ? rowData.LongName : translations['short_not_assigned'] || 'n/a'}</Typography>;
                }
            },
            {
                title: translations["short_name"] || 'Short name', field: 'ShortName',
                hidden: visible ? visible['ShortName'] : false,
                filtering: true,
                render: rowData => {
                    return <Typography variant={'caption'}>{rowData.ShortName ? rowData.ShortName : translations['short_not_assigned'] || 'n/a'}</Typography>;
                }
            },
            {
                title: translations["dedicated_for"] || 'Dedicated for', field: 'DedicatedFor',
                hidden: visible ? visible['DedicatedFor'] : false,
                lookup: { 'magnetic': translations['magnetic'] || 'Magnetic', 'flash': translations['flash'] || 'Flash' },
                defaultFilter: ['magnetic', 'flash'],
                filtering: true,
                width: 100,
                render: rowData => {
                    return <Typography variant={'caption'}>{rowData.DedicatedFor ? getDedicatedFor(rowData.DedicatedFor) : translations['short_not_assigned'] || 'n/a'}</Typography>;
                }
            },
            {
                title: translations["info_text"] || 'Info text', field: 'Description',
                hidden: visible ? visible['Description'] : false,
                filtering: true,
                width: 200,
                render: rowData => {
                    return <Typography variant={'caption'}>{rowData.Description ? rowData.Description : translations['short_not_assigned'] || 'n/a'}</Typography>;
                }
            },
        ];
    }, [translations, visible, permissions, getDedicatedFor])

    if (!hasAtLeastOnePermission(permissions, ['customer_erasure_method_load'])) {
        tableColumns.shift();
    }

    const redirectHandler = (path, ErasureMethodID = null) => {
        createHistory();
        setRedirectOptions({ redirect: true, ErasureMethodID: ErasureMethodID, redirectPath: path })
    };

    if (redirectOptions.redirect) {
        return <Redirect to={redirectOptions.redirectPath} />;
    }

    return (
        <div className={classes.root}>
            {hasPermission(permissions, 'customer_erasure_method_list') ?
                <CustomerErasureMethodList
                    tableColumns={tableColumns}
                    tableRef={tableRef}
                />
                :
                null}
        </div>
    );
};

const mapStateToProps = state => {
    return {
        translations: state.translate && state.translate.translations ? state.translate.translations.activeTranslations || {} : {},
        columns: state.core.login.userSettings ? state.core.login.userSettings.Columns : null,
        permissions: state?.core?.login?.permissions || [],
        activeCustomerID: state.core.login.activeCustomer.CustomerID,
    };
};

export default connect(
    mapStateToProps,
    null
)(withStyles(styles)(CustomerErasureMethod));
